import React, { useState } from 'react';
import { X } from 'lucide-react';

const inputStyle = { padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '13px', width: '100%' };
const labelStyle = { fontSize: '12px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '4px' };

export default function SimpleIntentEditDialog({ intent, onSubmit, onCancel, onToast }) {
  const [label, setLabel] = useState(intent.label || '');
  const [active, setActive] = useState(intent.active !== false);
  const [expiresAt, setExpiresAt] = useState(intent.expires_at ? intent.expires_at.slice(0, 10) : '');
  const [maxUses, setMaxUses] = useState(intent.max_uses ?? '');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit({
        label: label || undefined,
        active,
        expires_at: expiresAt || null,
        max_uses: maxUses !== '' ? parseInt(maxUses, 10) : null,
      });
      if (onToast) { onToast('Intent updated'); }
      onCancel();
    } catch {
      if (onToast) { onToast('Failed to update intent', 'error'); }
    } finally {
      setSubmitting(false);
    }
  };

  const typeLabel = (intent.intent_type || 'intent').replace(/_/g, ' ');

  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div style={{
        backgroundColor: '#fff', borderRadius: '12px', padding: '24px',
        maxWidth: '480px', width: '100%', boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '18px' }}>Edit Intent</h3>
          <button onClick={onCancel} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
            <X size={18} color="#6b7280" />
          </button>
        </div>

        <div style={{ marginBottom: '12px', padding: '6px 10px', backgroundColor: '#f3f4f6', borderRadius: '6px', fontSize: '12px', color: '#6b7280' }}>
          Type: <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{typeLabel}</span>
          {intent.code && <> &middot; Code: <code style={{ fontWeight: 600 }}>{intent.code}</code></>}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div>
            <label style={labelStyle}>Label</label>
            <input type="text" value={label} onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g., Spring Campaign — Signup" style={inputStyle} />
          </div>

          <div>
            <label style={labelStyle}>Expires At</label>
            <input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)}
              style={inputStyle} />
          </div>

          <div>
            <label style={labelStyle}>Max Uses</label>
            <input type="number" value={maxUses} onChange={(e) => setMaxUses(e.target.value)}
              placeholder="Unlimited" min="0" style={inputStyle} />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ ...labelStyle, marginBottom: 0, cursor: 'pointer' }} htmlFor="intent-active-toggle">Active</label>
            <input id="intent-active-toggle" type="checkbox" checked={active}
              onChange={(e) => setActive(e.target.checked)}
              style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button onClick={onCancel} style={{
            padding: '8px 16px', borderRadius: '6px', border: '1px solid #d1d5db',
            backgroundColor: '#fff', cursor: 'pointer', fontSize: '14px',
          }}>Cancel</button>
          <button
            disabled={submitting}
            onClick={handleSubmit}
            style={{
              padding: '8px 16px', borderRadius: '6px', border: 'none',
              backgroundColor: '#3b82f6', color: '#fff', cursor: submitting ? 'not-allowed' : 'pointer',
              fontSize: '14px', fontWeight: 600, opacity: submitting ? 0.6 : 1,
            }}
          >{submitting ? 'Saving...' : 'Save Changes'}</button>
        </div>
      </div>
    </div>
  );
}
